import { create } from 'zustand';
import { auth } from '@mindstudio-ai/interface';
import api from './api';
import { getOrCreateVisitorId } from './visitorId';
import { PRICING_TIERS, coercePlanId } from '../../../methods/src/common/pricing';
import type {
  ProposedFraming,
  ConfirmedFraming,
} from '../../../methods/src/common/ideaFraming/types';

/**
 * Race a promise against a timeout. Used to guard the initial profile fetch:
 * if `getMe` stalls (a platform latency blip), the app shell must NOT sit
 * behind the full-screen loader forever waiting on it. On timeout we reject so
 * the caller can flip `ready` and render — the user is already authenticated
 * (session cookie present); the profile just fills in on a later refresh.
 * Normal `getMe` returns in well under a second, so a 10s ceiling never trips
 * on a healthy call.
 */
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('getMe timeout')), ms);
    promise.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        clearTimeout(timer);
        reject(e);
      },
    );
  });
}
const PROFILE_FETCH_TIMEOUT_MS = 10000;

/**
 * Backoff schedule (ms) for auto-retrying a stalled or failed profile fetch.
 * When `getMe` times out (a platform latency blip), the app renders immediately
 * with no profile — which strips the user's name (the avatar falls back to "Y")
 * and hides role-gated UI like the admin portal, even though the user is still
 * authenticated. Without a retry that degraded state persists until the user
 * happens to change route or refocus the tab, so it can look as if they were
 * silently swapped to a different, non-admin account. These retries heal it on
 * their own, restoring the name and admin gating within ~a minute of recovery.
 */
const PROFILE_RETRY_DELAYS_MS = [2000, 4000, 8000, 15000, 30000];

/**
 * Monotonic token so only the most recent profile load stays live: a new auth
 * transition or a manual refresh supersedes any in-flight retry loop, and a
 * logout cancels it outright.
 */
let profileLoadToken = 0;

// ---------- Auth ----------
export interface AppUser {
  id: string;
  email: string | null;
  phone: string | null;
  roles: string[];
  apiKey: string | null;
  createdAt: string;
}

export interface ProfileUser {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  // Legacy free-text fields
  location?: string;
  technicalBackground?: string;
  areasOfInterest?: string[];
  // Structured profile fields (alpha rollout)
  firstName?: string;
  lastName?: string;
  country?: string;
  /** Preferred currency for the Business Case Memo (ISO 4217 code, e.g. "EUR"). */
  currency?: string;
  stateProvince?: string;
  profession?: string;
  educationLevel?: string;
  profileCompletedAt?: number;
  /** Unverified contact email captured from phone sign-ups. */
  contactEmail?: string | null;
  /**
   * Timestamp when the user accepted EntreDash's Confidentiality
   * Terms. Used by `<ConfidentialityModal />` to decide whether to
   * gate the user's next sensitive action (first idea capture / first
   * strategy message) behind a one-time consent dialog.
   */
  confidentialityAcknowledgedAt?: number;
  // Roles assigned by the platform; used to gate the admin portal
  // client-side. The server enforces the same `admin` check
  // independently, so a tampered roles array can't grant access.
  roles?: string[];
  subscriptionStatus: 'none' | 'active' | 'past_due' | 'canceled';
  /** Support grace window, honored by `hasPaidAccess` exactly as the backend does. */
  subscriptionGraceUntil?: number;
  /** Credit-pack member window (Education Center and perks for 12 months; not a plan). */
  perkAccessUntil?: number;
  /**
   * Operator-granted free tester pass. Unlocks paid features "as if subscribed"
   * for evaluation (the metered budget is enforced server-side). Honored by
   * `hasPaidAccess` here, exactly as the backend does. Arrives from `getMe` as
   * `1`/`true` for a tester, or absent — so it is read as truthy, never `=== true`.
   */
  testerPass?: boolean;
  /** VIP tester: how many ideas the pass covers end to end (set by a VIP invite). */
  testerIdeaBudget?: number | null;
  /** Operator-set internal/team account marker. */
  isInternal?: boolean;
  subscriptionPlan?: string;
  subscriptionInterval?: 'annual' | 'monthly';
  /**
   * The MONTHLY-normalized amount actually billed, in cents. Present once the
   * subscription has synced from Stripe. Used instead of the pricing table so a
   * grandfathered subscriber is never quoted a price they do not pay.
   */
  subscriptionPriceCents?: number;
  /** True when this account subscribed during the founding-rate window. */
  isFoundingMember?: boolean;
  /**
   * A plan change queued for the renewal date (a downgrade, an annual→monthly
   * switch, or a cancellation, where `pendingPlan` is `'free'`). Display only:
   * the user keeps their current plan in full until the effective date, so
   * nothing here is ever consulted for entitlement.
   */
  pendingPlan?: 'free' | 'inventor' | 'plus' | 'startup';
  pendingPlanInterval?: 'annual' | 'monthly';
  pendingPlanEffectiveAt?: number;
  subscriptionPeriodEnd?: number;
  streakCount: number;
  streakLastDay?: number;
  xp: number;
  level: 'Inventor' | 'Practitioner' | 'Specialist' | 'Strategist';
  memoNotificationsEmail: boolean;
  dailyDigestEmail: boolean;
  deadlineReminderEmail?: boolean;
}

interface AuthState {
  user: AppUser | null;
  profile: ProfileUser | null;
  ready: boolean;
  setUser: (user: AppUser | null) => void;
  setProfile: (profile: ProfileUser | null) => void;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

export const useAuth = create<AuthState>((set, get) => ({
  user: auth.getCurrentUser(),
  profile: null,
  ready: false,
  setUser: (user) => set({ user }),
  setProfile: (profile) => set({ profile }),
  refresh: async () => {
    const cur = auth.getCurrentUser();
    set({ user: cur });
    if (!cur) {
      set({ profile: null, ready: true });
      return;
    }
    // Fetch the profile with auto-retry so a slow or failed load self-heals
    // instead of stranding the UI without the user's name or admin access.
    await loadProfile();
  },
  logout: async () => {
    // Cancel any in-flight profile retry loop so it can't repopulate the store
    // after we clear it.
    profileLoadToken++;
    await auth.logout();
    set({ user: null, profile: null });
  },
}));

/**
 * Load the current user's profile into the auth store, retrying on the backoff
 * schedule if the fetch stalls or fails. This guarantees a transient platform
 * outage can't leave the UI stuck without the user's name or role-gated
 * features (the admin portal) — the profile fills in on its own once the
 * platform recovers, with no route change or tab refocus required.
 */
async function loadProfile(): Promise<void> {
  const myToken = ++profileLoadToken;
  for (let attempt = 0; attempt <= PROFILE_RETRY_DELAYS_MS.length; attempt++) {
    // A newer load (route change, tab focus, sign-out) has superseded this one.
    if (myToken !== profileLoadToken) return;
    const cur = auth.getCurrentUser();
    if (!cur) {
      useAuth.setState({ user: null, profile: null, ready: true });
      return;
    }
    try {
      const result = await withTimeout(
        // The visitor id links this browser's visitor-log row to the account while the profile is unfinished.
        api.getMe({ visitorId: getOrCreateVisitorId() ?? undefined }),
        PROFILE_FETCH_TIMEOUT_MS,
      );
      if (myToken !== profileLoadToken) return; // superseded while awaiting
      useAuth.setState({ profile: (result.user as ProfileUser | null) ?? null, ready: true });
      return; // got the profile — done
    } catch {
      if (myToken !== profileLoadToken) return;
      // Render the shell now (the user is authenticated); keep any prior
      // profile rather than blanking it. Then wait and try again so the name
      // and role gating self-heal.
      useAuth.setState({ ready: true });
      if (attempt < PROFILE_RETRY_DELAYS_MS.length) {
        await new Promise((resolve) => setTimeout(resolve, PROFILE_RETRY_DELAYS_MS[attempt]));
      }
    }
  }
}

// Wire auth state changes into the store
auth.onAuthStateChanged(async (user) => {
  useAuth.setState({ user });
  if (user) {
    // Auto-retrying load: a transient outage can't leave the user without their
    // name or role-gated UI (the admin portal) after a sign-in or token refresh.
    await loadProfile();
  } else {
    // Cancel any in-flight retry loop from a previous session before clearing.
    profileLoadToken++;
    useAuth.setState({ profile: null, ready: true });
  }
});

// ---------- Anonymous session (localStorage) ----------
export interface AnonMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
}

export interface AnonExtractedFacts {
  technicalField?: string;
  noveltyStatement?: string;
  alternatives?: string[];
  cpcClassifications?: string[];
  searchAxes?: string[];
  commercialContext?: string;
  filingUrgency?: string;
}

/**
 * Mirror of the backend `StageState` shape, persisted alongside the
 * anonymous chat in localStorage so /try can render the same 7-step
 * progress stepper that signed-in users see. Kept as a structural
 * type (not an import of the backend type) so this file stays free
 * of any cross-package coupling — the backend writes JSON that
 * matches this contract, and the frontend reads it back as-is.
 */
export interface AnonStageState {
  current: string;
  covered: string[];
}

/**
 * Loose mirror of the backend `NeedsDiscoveryFacts` accumulator. Kept
 * structural (not a backend import) so this file stays free of
 * cross-package coupling — the Product Strategist chat turn returns this
 * JSON and we persist it back as-is, then hand it to the claim method.
 */
export type AnonDiscoveryFacts = Record<string, unknown>;

export interface AnonState {
  ideaText: string;
  messages: AnonMessage[];
  /**
   * Patent-session accumulator. Retained for backward compatibility with
   * any anon state stored before the front door switched to the Product
   * Strategist; the live front-door flow populates `discoveryFacts`.
   */
  extractedFacts: AnonExtractedFacts;
  /**
   * Product Strategist (Needs Discovery) accumulator. This is what the
   * live anonymous front door writes and what the claim method consumes.
   */
  discoveryFacts?: AnonDiscoveryFacts;
  sessionComplete: boolean;
  /**
   * Persisted stage progress. Optional because legacy stored anon
   * states (from before this field existed) won't have it; callers
   * default to the Product Strategist's opening stage (`need`) for any
   * absent state.
   */
  stageState?: AnonStageState;
  /**
   * Coaching intensity chosen on the tone card before the first question.
   *
   * The anonymous flow has no session row, so the choice lives here and rides
   * on every turn request. Optional because visitors who were mid-conversation
   * before the tone card existed have no stored choice — those sessions keep
   * running at the backend default rather than being interrupted to pick one.
   */
  tone?: 'gentle' | 'balanced' | 'challenge';
  /**
   * The demonstrated-familiarity read ("Speak My Level") for the /try flow.
   * Seeded from the framing turn's classification of the idea text, carried on
   * every chat turn, and changeable from the header chip. Absent => calibrating.
   */
  languageRegister?: 'plain' | 'calibrating' | 'fluent';
  /**
   * Idea Framing (see idea-framing-industry-stage.md). The signed-in flow parks
   * this transient state on the Session row; the anonymous flow has no session,
   * so the same three pieces live here in browser storage instead.
   *
   * - `awaitingFraming` — the framing slip is up and the opening conversation
   *   turn has not run yet. A typed reply while this is true is interpreted as a
   *   confirm/correction, not a normal chat message.
   * - `proposedFraming` — the classifier's current read shown in the slip
   *   (re-proposed on each typed correction). Transient; never the source of
   *   truth once confirmed.
   * - `confirmedFraming` — set the moment the visitor confirms. Drives the
   *   collapsed receipt and is written onto the real idea record at sign-up
   *   (`claimNeedsDiscoveryAnonymousSession`), exactly as the tone choice is.
   */
  awaitingFraming?: boolean;
  proposedFraming?: ProposedFraming;
  confirmedFraming?: ConfirmedFraming;
}

const ANON_KEY = 'patently:try:state';

export function readAnonState(): AnonState | null {
  try {
    const raw = localStorage.getItem(ANON_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AnonState;
  } catch {
    return null;
  }
}

export function writeAnonState(state: AnonState): void {
  try {
    localStorage.setItem(ANON_KEY, JSON.stringify(state));
  } catch {
    // localStorage may be unavailable in incognito or quota-full
  }
}

export function clearAnonState(): void {
  try {
    localStorage.removeItem(ANON_KEY);
  } catch {
    // ignore
  }
}

// ---------- Toasts ----------
export interface Toast {
  id: number;
  message: string;
  variant?: 'brass' | 'success' | 'error';
  /** Optional inline action (e.g. Undo a destructive change). */
  action?: { label: string; onClick: () => void };
  /** Creation time, rendered as the slip's mono dateline. */
  at: number;
  /**
   * Exit animation has started. The row stays mounted for TOAST_EXIT_MS so the
   * animation can play, then is removed. Without this the node was spliced out
   * of the array and simply vanished — there was no exit animation at all.
   */
  leaving?: boolean;
}

interface ShowOptions {
  /** Optional inline action button rendered inside the toast. */
  action?: { label: string; onClick: () => void };
  /**
   * Auto-dismiss delay in ms. Defaults to 4500, or 8000 when the toast carries
   * an action — 4500ms can expire while the pointer is still travelling toward
   * an "Undo", which loses the user work the affordance existed to protect.
   */
  durationMs?: number;
}

interface ToastState {
  toasts: Toast[];
  show: (
    message: string,
    variant?: Toast['variant'],
    options?: ShowOptions,
  ) => void;
  /** Begin the exit animation, then unmount. */
  dismiss: (id: number) => void;
  /** Hold the auto-dismiss timer while the pointer or focus is on the toast. */
  pause: (id: number) => void;
  /** Resume with the time that was left when it was paused. */
  resume: (id: number) => void;
}

let toastCounter = 1;

/** Must match the .toast--leaving animation duration in styles.css. */
const TOAST_EXIT_MS = 180;
/**
 * Most slips on screen at once. Admin batch actions can fire several in a row;
 * beyond three the deck stops being glanceable and starts covering the page.
 */
const TOAST_MAX = 3;

/**
 * Live auto-dismiss timers, keyed by toast id. Kept outside the store because
 * they are scheduling state, not render state — putting them in the store would
 * retrigger every subscriber on each pause and resume.
 */
const timers = new Map<number, { handle: number; endsAt: number; remaining: number }>();

function clearTimer(id: number) {
  const t = timers.get(id);
  if (t) {
    clearTimeout(t.handle);
    timers.delete(id);
  }
}

export const useToasts = create<ToastState>((set, get) => {
  /** Schedule (or reschedule) a toast's auto-dismiss `ms` from now. */
  function arm(id: number, ms: number) {
    clearTimer(id);
    const handle = window.setTimeout(() => {
      timers.delete(id);
      get().dismiss(id);
    }, ms);
    timers.set(id, { handle, endsAt: Date.now() + ms, remaining: ms });
  }

  return {
    toasts: [],
    show: (message, variant = 'brass', options) => {
      const id = toastCounter++;
      set((s) => {
        // Over the cap, flip the oldest still-live slip to leaving so it
        // animates out, rather than splicing it and having it disappear.
        const live = s.toasts.filter((t) => !t.leaving);
        let toasts = s.toasts;
        if (live.length >= TOAST_MAX) {
          const oldest = live[0];
          clearTimer(oldest.id);
          toasts = toasts.map((t) => (t.id === oldest.id ? { ...t, leaving: true } : t));
          window.setTimeout(
            () => set((s2) => ({ toasts: s2.toasts.filter((t) => t.id !== oldest.id) })),
            TOAST_EXIT_MS,
          );
        }
        return {
          toasts: [
            ...toasts,
            { id, message, variant, action: options?.action, at: Date.now() },
          ],
        };
      });
      arm(id, options?.durationMs ?? (options?.action ? 8000 : 4500));
    },
    dismiss: (id) => {
      clearTimer(id);
      // Two phases: mark leaving (animation plays), then unmount.
      set((s) => s.toasts.some((t) => t.id === id && !t.leaving)
        ? { toasts: s.toasts.map((t) => (t.id === id ? { ...t, leaving: true } : t)) }
        : s);
      window.setTimeout(
        () => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
        TOAST_EXIT_MS,
      );
    },
    pause: (id) => {
      const t = timers.get(id);
      if (!t) return;
      clearTimeout(t.handle);
      // Remember what was left so resume does not restart the full duration.
      timers.set(id, { ...t, remaining: Math.max(0, t.endsAt - Date.now()) });
    },
    resume: (id) => {
      const t = timers.get(id);
      if (!t) return;
      arm(id, t.remaining);
    },
  };
});

// ============ Auth modal ============
// Global control for the sign in / sign up popup. Any "Sign in" / "Sign up"
// trigger (nav, mobile drawer, closing CTA) calls openAuthModal('signin' |
// 'signup') to pop the verification modal on the current page, instead of
// navigating to the full /signin or /signup route. The full routes remain as
// a fallback (deep links, "must sign in" redirects).
interface AuthModalState {
  open: boolean;
  mode: 'signin' | 'signup';
  // Optional post-auth redirect target (a plain path like
  // "/settings/subscription?plan=inventor"). When set, the modal routes here
  // after verification instead of the default /dashboard, and carries it
  // through the full-page onboarding for brand-new accounts. Used by the
  // pricing plan buttons so a chosen plan survives signup.
  next: string | null;
  openModal: (mode: 'signin' | 'signup', next?: string | null) => void;
  close: () => void;
}

export const useAuthModal = create<AuthModalState>((set) => ({
  open: false,
  mode: 'signin',
  next: null,
  openModal: (mode, next = null) => set({ open: true, mode, next }),
  close: () => set({ open: false }),
}));


/**
 * Whether this profile currently has paid access. MIRRORS the backend
 * `hasPaidAccess` in `dist/methods/src/common/auth.ts` — the two must agree.
 *
 * It exists because 12 components were each testing
 * `profile?.subscriptionStatus === 'active'` inline, which is not the rule the
 * backend enforces. Two cases diverged:
 *
 *  - OPERATORS (admin role) are comped to full access server-side, but had
 *    `subscriptionStatus: 'none'`, so every one of those 12 places nagged them
 *    to buy something they already had and disabled buttons the backend would
 *    have allowed.
 *  - A past_due user inside a support grace window keeps access on the server,
 *    and was locked out by the UI regardless.
 *
 * Use this rather than reading `subscriptionStatus` directly. A raw read is
 * almost always the access question in disguise, and gets these two wrong.
 */
export function hasPaidAccess(profile: ProfileUser | null | undefined): boolean {
  if (!profile) return false;
  if (profile.roles?.includes('admin')) return true;
  // Free tester pass unlocks paid features "as if subscribed", mirroring the
  // backend chokepoint. Truthy read (not `=== true`): it arrives from getMe as
  // the number 1. The metered evaluation budget is enforced server-side.
  if (profile.testerPass) return true;
  if (profile.subscriptionStatus === 'active') return true;
  return (profile.subscriptionGraceUntil ?? 0) > Date.now();
}

/**
 * Member access to the Education Center (and other member perks): a plan, or a credit
 * pack's 12-month member window. Mirrors the backend `hasPaidAccess`; plan allowances
 * and exports follow `hasPaidAccess` above, which leaves the pack window out.
 */
export function hasMemberAccess(profile: ProfileUser | null | undefined): boolean {
  if (hasPaidAccess(profile)) return true;
  return (profile?.perkAccessUntil ?? 0) > Date.now();
}

/**
 * Display name of the user's current billing tier ("Starter" / "Standard" /
 * "Plus" / "Pro"), mirroring the backend `getUserTier`: no paid access → free
 * tier; admins resolve to the top tier; otherwise the subscription plan id.
 * Used to seed the over-cap dialog's upgrade card when the triggering error
 * doesn't name a tier (e.g. the per-idea regeneration cap).
 */
export function billingTierName(profile: ProfileUser | null | undefined): string {
  if (!hasPaidAccess(profile)) return PRICING_TIERS.free.displayName;
  if (profile?.roles?.includes('admin')) return PRICING_TIERS.startup.displayName;
  return PRICING_TIERS[coercePlanId(profile?.subscriptionPlan)].displayName;
}


/**
 * The member's account type, in plain words, for the account menu and the
 * nav footer ("Operator", "VIP tester", "Standard plan", "Starter · Free").
 * Display only: entitlement is always decided server-side.
 * Precedence: team roles, then internal, then tester passes, then billing.
 */
export function accountAccess(profile: ProfileUser | null | undefined): {
  label: string;
  detail: string;
  tone: 'operator' | 'team' | 'tester' | 'paid' | 'free';
} {
  const roles = profile?.roles ?? [];
  if (roles.includes('admin')) return { label: 'Operator', detail: 'Full access, including admin tools', tone: 'operator' };
  // Team roles: show every one the account holds ("Advisor team · Business development").
  const team = [roles.includes('consultant') && 'Advisor team', roles.includes('bd') && 'Business development'].filter(
    Boolean,
  ) as string[];
  if (team.length) {
    return {
      label: team.join(' · '),
      detail: profile?.isInternal ? 'EntreDash team account · Internal' : 'EntreDash team account',
      tone: 'team',
    };
  }
  if (profile?.isInternal) return { label: 'Internal', detail: 'EntreDash team account', tone: 'team' };
  if (profile?.testerPass) {
    const n = profile.testerIdeaBudget ?? 0;
    return n > 0
      ? { label: 'VIP tester', detail: `Paid features on ${n} idea${n === 1 ? '' : 's'}, free`, tone: 'tester' }
      : { label: 'Tester', detail: 'Free tester pass for paid features', tone: 'tester' };
  }
  if (profile && hasPaidAccess(profile)) {
    const plan = PRICING_TIERS[coercePlanId(profile.subscriptionPlan)].displayName;
    const detail =
      profile.subscriptionStatus === 'past_due'
        ? 'Payment needs attention'
        : profile.subscriptionStatus !== 'active'
          ? 'Grace period'
          : profile.isFoundingMember
            ? 'Founding member'
            : profile.subscriptionInterval === 'annual'
              ? 'Billed yearly'
              : 'Billed monthly';
    return { label: `${plan} plan`, detail, tone: 'paid' };
  }
  return { label: `${PRICING_TIERS.free.displayName} · Free`, detail: 'Upgrade anytime for more', tone: 'free' };
}

// The static blog pages can't ask who's signed in; they read this flag to hide the mobile sign-up bar.
useAuth.subscribe((s) => {
  try {
    if (s.user) localStorage.setItem('ed.signedIn', '1');
    else if (s.ready) localStorage.removeItem('ed.signedIn');
  } catch {
    /* storage unavailable */
  }
});
